import React from 'react';
import { Target } from '@phosphor-icons/react';

interface GoalCompletionData {
    completedGoals: number;
    totalGoals: number;
    completionRate: number;
}

interface CoachingGoalCompletionWidgetProps {
    data?: GoalCompletionData[] | null;
    loading?: boolean;
    error?: string;
}

export const CoachingGoalCompletionWidget: React.FC<CoachingGoalCompletionWidgetProps> = ({ data, loading, error }) => {
    const row = Array.isArray(data) ? data[0] : null;
    const rate = Number(row?.completionRate) || 0;
    const completed = Number(row?.completedGoals) || 0;
    const total = Number(row?.totalGoals) || 0;
    const inProgress = total - completed;

    // Determine color based on rate
    const ringColor = rate >= 70 ? '#059669' : rate >= 40 ? '#D97706' : '#EF4444';
    const ringBg = rate >= 70 ? 'text-emerald-600' : rate >= 40 ? 'text-amber-600' : 'text-red-500';

    return (
        <div className="flex flex-col h-full p-5">
            <div className="flex items-center justify-between mb-4">
                <span className="text-xs text-gray-400">Organization-wide</span>
            </div>
            <div className="flex-1">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : !row ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <Target className="w-8 h-8" />
                        <span className="text-sm">No goal data</span>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-5">
                        {/* Donut chart */}
                        <div className="relative w-28 h-28">
                            <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                                <path
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none" stroke="#e5e7eb" strokeWidth="3.5"
                                />
                                <path
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none" stroke={ringColor} strokeWidth="3.5"
                                    strokeDasharray={`${rate}, 100`}
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className={`text-2xl font-bold ${ringBg}`}>{rate}%</span>
                            </div>
                        </div>

                        {/* Stat cards */}
                        <div className="grid grid-cols-3 gap-3 w-full max-w-xs">
                            <div className="text-center p-3 bg-emerald-50 rounded-lg">
                                <div className="text-xl font-bold text-emerald-600">{completed}</div>
                                <div className="text-[10px] text-gray-400">Completed</div>
                            </div>
                            <div className="text-center p-3 bg-blue-50 rounded-lg">
                                <div className="text-xl font-bold text-blue-600">{inProgress > 0 ? inProgress : 0}</div>
                                <div className="text-[10px] text-gray-400">In Progress</div>
                            </div>
                            <div className="text-center p-3 bg-gray-50 rounded-lg">
                                <div className="text-xl font-bold text-gray-600">{total}</div>
                                <div className="text-[10px] text-gray-400">Total Goals</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
